import React, { FC, ChangeEvent, useEffect, useState, useCallback, useRef } from 'react';

import { Form } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import clsx from 'clsx';

import {
  FormGroup,
  FormControl,
  FormText,
  DivCheckboxBlock,
  SearchIcon,
  ButtonCloseSearch,
  DivWrapper
} from './styles';
import { ClearButton } from '../ClearButton';
import { FilterLayout } from '../FilterLayout';
import useDebounce from '../../../../util/useDebounce';
import { getCurrentTextBoxByName } from '../../utils';
import {
  TCurrentFilter,
  TCurrentTextBox,
  TCurrentTextBoxIsLoadingState,
  TCurrentTextBoxValue
} from '../../../../commonTypes/filters';
import { FnLoader } from '../../../../components/elements/FnLoader/index';
import { Checkboxes } from '../Checkboxes';
import FnPerfectScrollbar from '../../../../components/elements/FnPerfectScrollbar/index';
import { FnPerfectScrollbarVariants } from '../../../../components/elements/FnPerfectScrollbar/constants';
import { TActiveQuickFilter } from '@features/Filters/redux/types';

interface Props {
  name: string;
  currentFilterItems: TCurrentFilter[];
  currentTextBoxItems: TCurrentTextBox[];
  isLoadingStates: TCurrentTextBoxIsLoadingState[];
  onSetFilter: (data: TCurrentFilter) => void;
  onClearFilter: (data: string) => void;
  onClearCurrentTextBoxValues: (data: string[]) => void;
  onFetchDataTextBoxFilterAction: (internalName: string, searchValue: string) => void;
  onFetchCancel: (internalName: string) => void;
  onSetActiveQuickFilter: (data: TActiveQuickFilter) => void;
  isQuick?: boolean;
  activeQuickFilters?: TActiveQuickFilter;
  placeholder?: string;
  showColor?: boolean;
}

export const TextBox: FC<Props> = ({
  name,
  currentFilterItems,
  currentTextBoxItems,
  isLoadingStates,
  onSetFilter,
  onClearFilter,
  onClearCurrentTextBoxValues,
  onFetchDataTextBoxFilterAction,
  onFetchCancel,
  onSetActiveQuickFilter,
  isQuick = false,
  activeQuickFilters = null,
  placeholder = '',
  showColor = false
}: Props) => {
  const [currentValue, setCurrentValue] = useState('');
  const [checkboxes, setCheckboxes] = useState<TCurrentTextBoxValue[]>([]);
  const debouncedSearchTerm = useDebounce(currentValue.replace(/\s+/g, ' ').trim(), 500);
  const [isLoading, setIsLoading] = useState(false);
  const [isHidden, setIsHidden] = useState(isQuick);
  const [touched, setTouched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const blockRef = useRef<HTMLDivElement>(null);

  const handleChangeInput = (e: ChangeEvent) => {
    const target = e.target as HTMLInputElement;

    if (isLoading && debouncedSearchTerm !== target.value.trim()) {
      onFetchCancel(name);
      setIsLoading(false);
    }

    setCurrentValue(target.value);
  };

  const fetchData = (internalName: string, searchValue: string): void => {
    if (!touched) {
      // INFO: don't fetch data if not user interaction
      return;
    }

    onFetchDataTextBoxFilterAction(internalName, searchValue);
  }

  useEffect(() => {
    const isLoadingState = isLoadingStates.find((el) => el.internalName === name);
    if (isLoadingState) {
      setIsLoading(isLoadingState.isLoading);
    } else {
      setIsLoading(false);
    }
  }, [isLoadingStates]);

  useEffect(() => {
    const currentTextBox = getCurrentTextBoxByName(currentTextBoxItems, name);
    if (currentTextBox) {
      setCurrentValue(currentTextBox.inputValue);
      setCheckboxes(currentTextBox.values);
    } else {
      setCurrentValue('');
      setCheckboxes([]);
    }
  }, [currentTextBoxItems]);

  useEffect(() => {
    if (debouncedSearchTerm.length < 3) {
      return;
    }

    if (!activeQuickFilters) {
      fetchData(name, debouncedSearchTerm);
    } else {
      const activeFilter = {};
      activeFilter[name] = true;
      onSetActiveQuickFilter(activeFilter);
      fetchData(name, debouncedSearchTerm);
    }
  }, [debouncedSearchTerm]);

  const handleClearFilter = () => {
    onClearFilter(name);
    onClearCurrentTextBoxValues([name]);
  };

  /// Quick filter
  useEffect(() => {
    if (isQuick) {
      document.addEventListener('click', handleClickOutside);

      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isQuick]);

  const handleClickOutside = (e) => {
    if (
      isQuick &&
      inputRef.current &&
      blockRef.current &&
      !inputRef.current.contains(e.target) &&
      !blockRef.current.contains(e.target)
    ) {
      setIsHidden(true);
    }
  };

  const handleFocus = useCallback(() => {
    setTouched(true);

    if (isQuick) {
      setIsHidden(false);
    }
  }, [isQuick]);

  return (
    <>
      <FilterLayout>
        <Form>
          <FormGroup className={clsx({ isQuick })}>
            <SearchIcon size={12} />
            {!!currentValue.length && (
              <ButtonCloseSearch variant="light" className="filter-btn-close-search" onClick={handleClearFilter}>
                <X size={16} />
              </ButtonCloseSearch>
            )}
            <FormControl
              ref={inputRef}
              type="text"
              value={currentValue}
              className="filter-form-control"
              onChange={handleChangeInput}
              onFocus={handleFocus}
              placeholder={placeholder}
            />
            <DivCheckboxBlock className={clsx({ isHidden })} ref={blockRef}>
              <FnPerfectScrollbar component="div" styled variant={FnPerfectScrollbarVariants.Secondary}>
                <DivWrapper>
                  {!checkboxes.length && !isLoading ? (
                    <FormText className="filter-form-text">
                      {currentValue ? 'No result found' : 'Type text to search ...'}
                    </FormText>
                  ) : (
                    <Checkboxes
                      data={checkboxes}
                      name={name}
                      currentFilterItems={currentFilterItems}
                      onSetFilter={onSetFilter}
                      onClearFilter={onClearFilter}
                      showColor={showColor}
                    />
                  )}
                  {isLoading && <FnLoader />}
                </DivWrapper>
              </FnPerfectScrollbar>
            </DivCheckboxBlock>
          </FormGroup>
        </Form>
      </FilterLayout>
      {!isQuick && <ClearButton onClear={handleClearFilter} />}
    </>
  );
};
